import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type PropsWithChildren,
} from "react";
import { hideFrontChat } from "util/openFrontChat";

type DismissScoutHandler = () => void;

type ChatSurfaceCoordinatorContextValue = {
  /**
   * Register a handler that closes this Scout surface when another chat opens.
   * Returns an unsubscribe function — return it from a `useEffect` cleanup so
   * the handler is removed on unmount.
   */
  registerDismissScout: (handler: DismissScoutHandler) => () => void;
  /** Call before opening a Scout popover so Front support chat closes. */
  notifyScoutPopoverOpening: () => void;
  /** Call when Front's messenger panel becomes visible. */
  notifyFrontChatOpened: () => void;
};

const ChatSurfaceCoordinatorContext = createContext<ChatSurfaceCoordinatorContextValue | null>(
  null
);

/**
 * Ensures at most one bottom-left chat surface is open: Scout popover or Front
 * support chat. Scout triggers hide Front on open; Front visibility dismisses
 * Scout popovers/minimized bars without resetting conversation session.
 */
export function ChatSurfaceCoordinatorProvider({ children }: PropsWithChildren) {
  const dismissScoutHandlersRef = useRef(new Set<DismissScoutHandler>());

  const registerDismissScout = useCallback((handler: DismissScoutHandler) => {
    dismissScoutHandlersRef.current.add(handler);
    return () => {
      dismissScoutHandlersRef.current.delete(handler);
    };
  }, []);

  const dismissAllScoutSurfaces = useCallback(() => {
    for (const handler of dismissScoutHandlersRef.current) {
      handler();
    }
  }, []);

  const notifyScoutPopoverOpening = useCallback(() => {
    hideFrontChat();
  }, []);

  const notifyFrontChatOpened = useCallback(() => {
    dismissAllScoutSurfaces();
  }, [dismissAllScoutSurfaces]);

  const value = useMemo(
    () => ({
      registerDismissScout,
      notifyScoutPopoverOpening,
      notifyFrontChatOpened,
    }),
    [registerDismissScout, notifyScoutPopoverOpening, notifyFrontChatOpened]
  );

  return (
    <ChatSurfaceCoordinatorContext.Provider value={value}>
      {children}
    </ChatSurfaceCoordinatorContext.Provider>
  );
}

export function useChatSurfaceCoordinator(): ChatSurfaceCoordinatorContextValue | null {
  return useContext(ChatSurfaceCoordinatorContext);
}
